'use client';

import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from 'recharts';

import { useMemo } from 'react';

import {
	ChartContainer,
	ChartLegend,
	ChartLegendContent,
	ChartTooltip,
	ChartTooltipContent,
} from '@/components/ui/chart';
import { Skeleton } from '@/components/ui/skeleton';

import { cn } from '@/lib/utils';

import { LockedOverlay } from './locked-overlay';
import { StackedBarChartCardProps, StackedBarDataPoint } from './types';

// Dummy data for locked/preview state
const DUMMY_BAR_DATA: StackedBarDataPoint[] = [
	{ label: 'Category A', value1: 45, value2: 25 },
	{ label: 'Category B', value1: 35, value2: 20 },
	{ label: 'Category C', value1: 28, value2: 15 },
	{ label: 'Category D', value1: 22, value2: 12 },
	{ label: 'Category E', value1: 18, value2: 8 },
];

/**
 * Reusable stacked bar chart card component
 * Used for displaying stacked/grouped bar data (e.g., stage distribution with current vs historical)
 */
export function StackedBarChartCard({
	title,
	subtitle,
	data,
	series,
	isLocked = false,
	isLoading = false,
	showLegend = true,
	showXAxis = true,
	showYAxis = true,
	layout = 'horizontal',
	valueFormatter,
	height = 'h-64',
	className,
}: StackedBarChartCardProps) {
	// Use dummy data when locked to avoid exposing real data in DOM
	const displayData = useMemo(() => {
		if (isLocked) {
			return DUMMY_BAR_DATA.map((point, index) => ({
				label: point.label,
				...series.reduce(
					(acc, s, i) => ({
						...acc,
						[s.dataKey]: DUMMY_BAR_DATA[index]?.[`value${i + 1}`] ?? 0,
					}),
					{}
				),
			}));
		}
		return data;
	}, [isLocked, data, series]);

	const chartConfig = useMemo(
		() =>
			series.reduce(
				(acc, s) => ({
					...acc,
					[s.dataKey]: {
						label: s.label,
						color: s.color,
					},
				}),
				{}
			),
		[series]
	);

	if (isLoading) {
		return (
			<div className={cn('rounded-lg border border-border bg-card p-4', className)}>
				<Skeleton className="mb-2 h-5 w-32" />
				{subtitle && <Skeleton className="mb-4 h-4 w-48" />}
				<Skeleton className={cn('w-full', height)} />
			</div>
		);
	}

	return (
		<div className={cn('rounded-lg border border-border bg-card p-4', className)}>
			{/* Header */}
			<div className="mb-4">
				<h3 className="font-medium">{title}</h3>
				{subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
			</div>

			{/* Chart - Wrapped with locked overlay */}
			<LockedOverlay isLocked={isLocked}>
				{displayData?.length > 0 ? (
					<ChartContainer
						config={chartConfig}
						className={cn('[&]:aspect-auto', height)}
					>
						<BarChart
							data={displayData}
							layout={layout}
							margin={{
								top: 5,
								right: 10,
								bottom: 5,
								left: layout === 'vertical' ? 0 : 10,
							}}
						>
							<CartesianGrid
								strokeDasharray="3 3"
								horizontal={layout === 'horizontal'}
								vertical={layout === 'vertical'}
							/>
							{layout === 'horizontal' ? (
								<>
									{showXAxis && (
										<XAxis
											dataKey="label"
											tickLine={false}
											axisLine={false}
											tickMargin={8}
											tick={{ fontSize: 11 }}
											interval={0}
											angle={-45}
											textAnchor="end"
											height={60}
										/>
									)}
									{showYAxis && (
										<YAxis
											tickLine={false}
											axisLine={false}
											tick={{ fontSize: 10 }}
											width={40}
											tickFormatter={valueFormatter}
										/>
									)}
								</>
							) : (
								<>
									{showYAxis && (
										<YAxis
											dataKey="label"
											type="category"
											tickLine={false}
											axisLine={false}
											tick={{ fontSize: 11 }}
											width={70}
										/>
									)}
									{showXAxis && (
										<XAxis
											type="number"
											tickLine={false}
											axisLine={false}
											tick={{ fontSize: 10 }}
											tickFormatter={valueFormatter}
										/>
									)}
								</>
							)}
							<ChartTooltip
								cursor={{ fill: 'hsl(var(--muted))', opacity: 0.3 }}
								content={<ChartTooltipContent labelFormatter={(label) => label} />}
							/>
							{series.map((s) => (
								<Bar
									key={s.dataKey}
									dataKey={s.dataKey}
									fill={s.color}
									stackId="stack"
									radius={[2, 2, 0, 0]}
								>
									{s.colorKey &&
										displayData.map((entry, index) => {
											const colorValue = (entry as Record<string, string | number>)[s.colorKey!];
											return (
												<Cell
													key={`cell-${s.dataKey}-${index}`}
													fill={typeof colorValue === 'string' ? colorValue : s.color}
												/>
											);
										})}
								</Bar>
							))}
							{showLegend && <ChartLegend content={<ChartLegendContent />} />}
						</BarChart>
					</ChartContainer>
				) : (
					<div
						className={cn(
							'flex w-full items-center justify-center text-sm text-muted-foreground',
							height
						)}
					>
						No data available
					</div>
				)}
			</LockedOverlay>
		</div>
	);
}
